import apiClient from './apiClient';

export type DfrStatus = 'draft' | 'submitted' | 'approved' | 'returned';
export type DfrEntryType =
  | 'progress'
  | 'delay'
  | 'safety'
  | 'visitor'
  | 'materials'
  | 'equipment'
  | 'inspection'
  | 'quality_test'
  | 'statutory_visit'
  | 'non_conformance';

export interface DfrEntry {
  id: string;
  dfrId: string;
  taskId: string | null;
  entryType: DfrEntryType;
  description: string;
  documentId: string | null;
  fieldEvidenceId: string | null;
  inspectionId?: string | null;
  constructionTestResultId?: string | null;
  ncrId?: string | null;
  task?: {
    id: string;
    taskCode?: string | null;
    name?: string | null;
    lifecycleStatus?: string | null;
  } | null;
  inspection?: {
    id: string;
    inspectionNumber?: string | null;
    title?: string | null;
    inspectionType?: string | null;
    status?: string | null;
    completedAt?: string | null;
    resultSummary?: string | null;
    score?: number | null;
  } | null;
  clientUuid: string | null;
  createdAt: string;
}

export interface DailyFieldReport {
  id: string;
  projectId: string;
  reportDate: string;
  authorUserId: string | null;
  author?: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    role?: string | null;
  } | null;
  weather: string | null;
  temperatureC: number | null;
  manpowerCount: number;
  status: DfrStatus;
  workSummary: string | null;
  delaysConstraints: string | null;
  submittedAt: string | null;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  approvedBy?: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    role?: string | null;
  } | null;
  entries?: DfrEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface DfrEntryPayload {
  taskId?: string | null;
  entryType: DfrEntryType;
  description: string;
  documentId?: string | null;
  fieldEvidenceId?: string | null;
  inspectionId?: string | null;
  constructionTestResultId?: string | null;
  ncrId?: string | null;
  clientUuid?: string | null;
}

export interface DfrPayload {
  projectId: string;
  reportDate: string;
  weather?: string | null;
  temperatureC?: number | null;
  manpowerCount?: number;
  workSummary?: string | null;
  delaysConstraints?: string | null;
  clientUuid?: string | null;
  entries?: DfrEntryPayload[];
}

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  return Object.prototype.hasOwnProperty.call(body, 'data') ? body.data as T : body as T;
};

class DfrService {
  async list(params: { projectId?: string; status?: DfrStatus | ''; from?: string; to?: string } = {}): Promise<DailyFieldReport[]> {
    return unwrap<DailyFieldReport[]>(await apiClient.get('/dfr', { params }));
  }

  async get(id: string): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.get(`/dfr/${id}`));
  }

  async create(payload: DfrPayload): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.post('/dfr', payload));
  }

  async update(id: string, payload: Partial<DfrPayload>): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.patch(`/dfr/${id}`, payload));
  }

  async addEntry(id: string, payload: DfrEntryPayload): Promise<DfrEntry> {
    return unwrap<DfrEntry>(await apiClient.post(`/dfr/${id}/entries`, payload));
  }

  async removeEntry(entryId: string): Promise<void> {
    await apiClient.delete(`/dfr/entries/${entryId}`);
  }

  async submit(id: string): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.post(`/dfr/${id}/submit`, {}));
  }

  async approve(id: string): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.post(`/dfr/${id}/approve`, {}));
  }

  async return(id: string): Promise<DailyFieldReport> {
    return unwrap<DailyFieldReport>(await apiClient.post(`/dfr/${id}/return`, {}));
  }
}

export default new DfrService();
